// Zone breakdown component
// Shows zone-specific scores for metrics that support it
// Includes heatmap thumbnails and expandable UI

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { ZoneScores, MetricKey } from '@/types/analysis'
import { METRIC_INFO } from '@/data/metricExplanations'

interface ZoneBreakdownProps {
  metricKey: MetricKey
  zones: ZoneScores
  zoneHeatmaps?: Record<string, string>  // Zone heatmap URLs
  onZoneClick?: (zone: string) => void
  animated?: boolean
}

export function ZoneBreakdown({ metricKey, zones, zoneHeatmaps, onZoneClick, animated = true }: ZoneBreakdownProps) {
  const info = METRIC_INFO[metricKey]
  const zoneLabels = info.zoneLabels || {}

  // Convert zones to array and sort by score (lowest first = worst areas at top)
  const zoneEntries = Object.entries(zones)
    .filter(([_, score]) => score !== undefined)
    .sort((a, b) => (a[1] as number) - (b[1] as number))

  if (zoneEntries.length === 0) return null

  // Get color based on skin health score (higher = better)
  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#22C55E' // green - good
    if (score >= 40) return '#F59E0B' // amber - moderate
    return '#EF4444' // red - needs attention
  }

  // Find priority (worst) zone
  const priorityZone = zoneEntries[0]?.[0]

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-700">Zone Breakdown</h4>
      <div className="space-y-2.5">
        {zoneEntries.map(([zone, score], index) => {
          const concernScore = score as number
          const color = getScoreColor(concernScore)
          const label = zoneLabels[zone] || zone.replace(/([A-Z])/g, ' $1').trim()
          const isPriority = zone === priorityZone
          const heatmapUrl = zoneHeatmaps?.[zone]

          return (
            <button
              key={zone}
              onClick={() => onZoneClick?.(zone)}
              className={`w-full text-left group ${isPriority ? 'bg-red-50 rounded-lg p-2 -mx-2' : ''}`}
              disabled={!onZoneClick}
            >
              <div className="flex items-center gap-3">
                {/* Heatmap thumbnail */}
                {heatmapUrl && (
                  <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 border border-gray-200">
                    <img
                      src={heatmapUrl}
                      alt={`${label} heatmap`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors capitalize flex items-center gap-1.5">
                      {label}
                      {isPriority && (
                        <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">
                          Priority
                        </span>
                      )}
                    </span>
                    <span className="text-sm font-medium" style={{ color }}>
                      {concernScore}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: color }}
                      initial={animated ? { width: 0 } : undefined}
                      animate={{ width: `${concernScore}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.1 }}
                    />
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Expandable zone breakdown card for report page
interface ExpandableZoneBreakdownProps {
  title: string
  icon: string
  overallScore: number
  zones: ZoneScores
  zoneHeatmaps?: Record<string, string | undefined>  // Zone heatmap URLs (keys like 'forehead', 'nose', etc.)
  zoneLabels?: Record<string, string>
  photoUrl?: string  // User's photo for overlay
}

export function ExpandableZoneBreakdown({
  title,
  icon,
  overallScore,
  zones,
  zoneHeatmaps,
  zoneLabels = {},
  photoUrl,
}: ExpandableZoneBreakdownProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const zoneEntries = Object.entries(zones)
    .filter(([_, score]) => score !== undefined)
    .sort((a, b) => (a[1] as number) - (b[1] as number))

  if (zoneEntries.length === 0) return null

  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#22C55E'
    if (score >= 40) return '#F59E0B'
    return '#EF4444'
  }

  const priorityZone = zoneEntries[0]
  const priorityLabel = zoneLabels[priorityZone[0]] || priorityZone[0].replace(/([A-Z])/g, ' $1').trim()
  const priorityScore = priorityZone[1] as number
  const overallColor = getScoreColor(overallScore)

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <div className="text-left">
            <p className="font-medium text-gray-900">{title}</p>
            <p className="text-xs text-gray-500">
              Priority: <span className="capitalize" style={{ color: getScoreColor(priorityScore) }}>{priorityLabel}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-semibold" style={{ color: overallColor }}>{overallScore}</p>
            <p className="text-[10px] text-gray-400 uppercase">Overall</p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content - Grid layout with large heatmap images */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 border-t border-gray-100">
              {/* Grid of zone cards with large heatmaps */}
              <div className="grid grid-cols-2 gap-3">
                {zoneEntries.map(([zone, score], index) => {
                  const concernScore = score as number
                  const color = getScoreColor(concernScore)
                  const label = zoneLabels[zone] || zone.replace(/([A-Z])/g, ' $1').trim()
                  const isPriority = index === 0
                  const heatmapUrl = zoneHeatmaps?.[zone]

                  return (
                    <motion.div
                      key={zone}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`relative rounded-xl overflow-hidden ${isPriority ? 'ring-2 ring-red-400' : 'border border-gray-200'}`}
                    >
                      {/* Zone image with user photo + heatmap overlay */}
                      <div className="relative aspect-square w-full bg-gray-100">
                        {/* Base user photo */}
                        {photoUrl && (
                          <img
                            src={photoUrl}
                            alt="Your skin"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}

                        {/* Heatmap overlay at 60% opacity */}
                        {heatmapUrl && (
                          <img
                            src={heatmapUrl}
                            alt={`${label} analysis`}
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                              opacity: 0.6,
                              mixBlendMode: 'multiply',
                            }}
                          />
                        )}

                        {/* Fallback if no photo */}
                        {!photoUrl && !heatmapUrl && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                            <span className="text-4xl opacity-20">{icon}</span>
                          </div>
                        )}

                        {/* Priority badge */}
                        {isPriority && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-semibold shadow">
                            Priority
                          </div>
                        )}

                        {/* Score badge */}
                        <div
                          className="absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                          style={{ backgroundColor: color }}
                        >
                          {concernScore}
                        </div>

                        {/* Zone label at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                          <p className="text-white font-semibold text-sm capitalize drop-shadow-lg">{label}</p>
                          <div className="mt-1.5 h-1.5 bg-white/30 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full bg-white"
                              initial={{ width: 0 }}
                              animate={{ width: `${concernScore}%` }}
                              transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.05 }}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Compact inline zone chips
interface ZoneChipsProps {
  metricKey: MetricKey
  zones: ZoneScores
  selectedZone?: string
  onZoneSelect?: (zone: string) => void
}

export function ZoneChips({ metricKey, zones, selectedZone, onZoneSelect }: ZoneChipsProps) {
  const info = METRIC_INFO[metricKey]
  const zoneLabels = info.zoneLabels || {}

  const zoneEntries = Object.entries(zones).filter(([_, score]) => score !== undefined)

  if (zoneEntries.length === 0) return null

  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#22C55E'
    if (score >= 40) return '#F59E0B'
    return '#EF4444'
  }

  return (
    <div className="flex flex-wrap gap-2">
      {zoneEntries.map(([zone, score]) => {
        const displayScore = score as number
        const color = getScoreColor(score as number)
        const label = zoneLabels[zone] || zone.replace(/([A-Z])/g, ' $1').trim()
        const isSelected = selectedZone === zone

        return (
          <button
            key={zone}
            onClick={() => onZoneSelect?.(zone)}
            className={`
              px-3 py-1.5 rounded-full text-sm transition-all
              ${isSelected ? 'ring-2 ring-teal-500 ring-offset-1' : ''}
            `}
            style={{
              backgroundColor: `${color}15`,
              color: color,
            }}
          >
            <span className="capitalize">{label}</span>
            <span className="ml-1.5 font-semibold">{displayScore}%</span>
          </button>
        )
      })}
    </div>
  )
}
